/**
 * Version-check helpers for the force-update gate.
 *
 * Two independent signals drive the gate (see `useVersionCheck`):
 *  - **Native** (iOS/Android): compare the installed binary version against a
 *    server-published `minAppVersion`. Below the minimum → blocking gate.
 *  - **Web** (desktop/SPA): compare the deployed `buildId` (from /api/version)
 *    against the `buildId` baked into the loaded bundle. Different → reload.
 *
 * Everything here is pure and fails OPEN: any missing or unparseable input
 * resolves to "no action". A config glitch or a malformed version string must
 * never lock a user out of the app.
 */

/** Shape of the /api/version GET payload we consume (superset is fine). */
export interface VersionPayload {
  buildId?: string | null
  version?: string | null
  minAppVersion?: string | null
}

/**
 * Parse a dotted version (e.g. "1.2.3") into numeric segments. Prerelease /
 * build metadata after `-` or `+` is stripped ("1.2.3-beta.1" → [1,2,3]).
 * Returns null if any segment is not a non-negative integer.
 */
export function parseVersion(v: string | null | undefined): number[] | null {
  if (!v || typeof v !== 'string') return null
  const core = v.trim().split(/[-+]/)[0]
  if (!core) return null
  const nums = core.split('.').map((p) => Number(p))
  if (nums.length === 0 || nums.some((n) => !Number.isInteger(n) || n < 0)) return null
  return nums
}

/**
 * Semver-ish precedence compare. Missing trailing segments count as zero
 * ("1.2" == "1.2.0"). Returns -1 (a<b), 0 (equal / either unparseable), 1 (a>b).
 * Unparseable input resolves to 0 so callers never act on garbage.
 */
export function compareVersions(a: string, b: string): number {
  const pa = parseVersion(a)
  const pb = parseVersion(b)
  if (!pa || !pb) return 0
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0
    const db = pb[i] ?? 0
    if (da < db) return -1
    if (da > db) return 1
  }
  return 0
}

/**
 * True when the installed native version is strictly below the required
 * minimum. Fails open: missing/unparseable installed OR min → false (no gate).
 */
export function isUpdateRequired(
  installed: string | null | undefined,
  min: string | null | undefined,
): boolean {
  if (!installed || !min) return false
  if (!parseVersion(installed) || !parseVersion(min)) return false
  return compareVersions(installed, min) < 0
}

/**
 * True when the deployed web build differs from the loaded one. Both ids must
 * be present and non-empty; otherwise not stale (fail safe — no reload).
 */
export function isBuildStale(
  loaded: string | null | undefined,
  deployed: string | null | undefined,
): boolean {
  if (!loaded || !deployed) return false
  return loaded !== deployed
}
